import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { DistrictSchoolLink } from '../api/types';
import { DataGrid } from './DataGrid';
import { ErrorBanner, SuccessBanner } from './ui';
import { Button, TextInput } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';

export interface SchoolsTabProps {
  schools: DistrictSchoolLink[];
  loading: boolean;
  onReload: () => void;
  link: (nid: string) => Promise<unknown>;
  unlink: (nid: string) => Promise<unknown>;
}

export function SchoolsTab({ schools, loading, onReload, link, unlink }: SchoolsTabProps) {
  const navigate = useNavigate();
  const formId = useDomId('school');
  const [nid, setNid] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const onUnlink = async (target: string) => {
    if (!window.confirm(`Unlink school ${target} from this district?`)) return;
    setSaving(true);
    setError(null);
    try {
      await unlink(target);
      setMessage(`Unlinked ${target}.`);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<ColDef<DistrictSchoolLink>[]>(
    () => [
      { headerName: 'Name', field: 'name', minWidth: 220, flex: 2 },
      { headerName: 'NID', field: 'nid', maxWidth: 140 },
      { headerName: 'City', field: 'city', minWidth: 120 },
      { headerName: 'State', field: 'state', maxWidth: 90 },
      {
        headerName: '',
        maxWidth: 110,
        sortable: false,
        filter: false,
        cellRenderer: (p: ICellRendererParams<DistrictSchoolLink>) => (
          <button
            className="link-danger"
            onClick={(e) => {
              e.stopPropagation();
              if (p.data) onUnlink(p.data.nid);
            }}
          >
            Unlink
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const doLink = async () => {
    if (!nid.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await link(nid.trim());
      setMessage(`Linked ${nid.trim()}.`);
      setNid('');
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="toolbar">
        <h3>Schools ({schools.length})</h3>
      </div>

      <SuccessBanner message={message} />
      <ErrorBanner error={error} />

      <DataGrid<DistrictSchoolLink>
        rows={schools}
        columns={columns}
        loading={loading}
        getRowId={(r) => r.nid}
        onRowClick={(r) => navigate(`/highschools/${encodeURIComponent(r.nid)}`)}
        height={360}
      />

      <form
        className="entity-form"
        onSubmit={(e) => {
          e.preventDefault();
          void doLink();
        }}
      >
        <h4>Link a high school</h4>
        <div className="form-grid">
          <TextInput
            id={`${formId}-nid`}
            label="School NID"
            value={nid}
            onChange={setNid}
            placeholder="e.g. 88882USPU"
            helper="Enter the NID of a high school to associate"
          />
        </div>
        <div className="form-actions">
          <Button
            id={`${formId}-link`}
            text={saving ? 'Linking…' : 'Link school'}
            icon="link"
            onClick={() => void doLink()}
            loading={saving}
            disabled={!nid.trim()}
          />
        </div>
      </form>
    </div>
  );
}
